// Shared Cashmatic global callbacks
// Used by both console-new and console-sun apps

import {useSunmiStore} from "@/console-sun/stores";

/**
 * Registers all Cashmatic-related global callbacks.
 * Call this function from global.js in each app that needs Cashmatic support.
 */
export function registerCashmaticCallbacks() {
	// ==========================================
	// CASHMATIC DEVICE STATUS CALLBACK
	// ==========================================

	/**
	 * Called when Cashmatic device status is received.
	 * @param {string} ip - IP address of the Cashmatic
	 * @param {number} totalCoins - Total value of coins in cents
	 * @param {number} totalBills - Total value of bills in cents
	 * @param {number} totalAmount - Total amount in cents
	 * @param {boolean} pingSuccess - Whether the last ping was successful
	 * @param {number} lastPingTime - Timestamp of the last ping
	 */
	window.cashmaticDeviceStatusReceived = function (ip, totalCoins, totalBills, totalAmount, pingSuccess, lastPingTime) {
		let sunmiStore = useSunmiStore();
		sunmiStore.cashmaticDeviceStatus = {
			ip: ip,
			totalCoins: totalCoins,
			totalBills: totalBills,
			totalAmount: totalAmount,
			pingSuccess: pingSuccess,
			lastPingTime: lastPingTime
		};
	}

	// ==========================================
	// CASHMATIC PAYMENT/OPERATION CALLBACKS
	// ==========================================

	/**
	 * Called when a Cashmatic payment/operation starts or fails to start.
	 * @param {boolean} success - Whether the payment started successfully
	 * @param {string} errorMessage - Error message if failed
	 */
	window.cashmaticPaymentStarted = function (success, errorMessage, activeOperation) {
		let sunmiStore = useSunmiStore();
		if (success) {
			sunmiStore.cashmaticPaymentState = 'started';
		} else {
			sunmiStore.cashmaticPaymentState = 'error';
			sunmiStore.cashmaticPaymentError = errorMessage || 'Failed to start';
			sunmiStore.cashmaticActiveOperation = activeOperation || null;
		}
	}

	/**
	 * Called periodically during payment with status updates.
	 * @param {number} requested - Amount requested in cents
	 * @param {number} inserted - Amount inserted so far in cents
	 * @param {number} dispensed - Change dispensed in cents
	 * @param {number} notDispensed - Change that couldn't be dispensed in cents
	 * @param {string} status - Current status string
	 * @param {string} operation - Current operation type
	 */
	window.cashmaticPaymentStatus = function (requested, inserted, dispensed, notDispensed, status, operation) {
		let sunmiStore = useSunmiStore();
		sunmiStore.cashmaticPaymentStatus = {
			requested: requested,
			inserted: inserted,
			dispensed: dispensed,
			notDispensed: notDispensed,
			status: status,
			operation: operation
		};
	}

	/**
	 * Called when a Cashmatic payment completes (reaches idle state).
	 * @param {boolean} success - Whether the payment was successful
	 * @param {object} data - Final transaction data
	 */
	window.cashmaticPaymentCompleted = function (success, data) {
		let sunmiStore = useSunmiStore();
		sunmiStore.cashmaticPaymentState = success ? 'completed' : 'error';
		sunmiStore.cashmaticPaymentResult = data;
		if (!success && data?.error) {
			sunmiStore.cashmaticPaymentError = data.error;
		}
	}

	/**
	 * Called when a Cashmatic payment is cancelled.
	 * @param {boolean} success - Whether cancellation was successful
	 * @param {string} errorMessage - Error message if failed
	 */
	window.cashmaticPaymentCancelled = function (success, errorMessage) {
		let sunmiStore = useSunmiStore();
		if (success) {
			sunmiStore.cashmaticPaymentState = 'cancelled';
		} else {
			sunmiStore.cashmaticPaymentState = 'error';
			sunmiStore.cashmaticPaymentError = errorMessage || 'Failed to cancel';
		}
	}

	/**
	 * Called when a Cashmatic payment is force-committed.
	 * @param {boolean} success - Whether commit was successful
	 * @param {string} errorMessage - Error message if failed
	 */
	window.cashmaticPaymentCommitted = function (success, errorMessage) {
		let sunmiStore = useSunmiStore();
		if (success) {
			sunmiStore.cashmaticPaymentState = 'committed';
		} else {
			sunmiStore.cashmaticPaymentError = errorMessage || 'Failed to commit';
		}
	}

	// ==========================================
	// CASHMATIC FLOAT LEVEL CALLBACKS
	// ==========================================

	/**
	 * Called when float levels are received.
	 * @param {boolean} success - Whether the request succeeded
	 * @param {Object} coinsData - Float level data for coins
	 * @param {Object} notesData - Float level data for notes
	 */
	window.cashmaticFloatLevelsReceived = function (success, coinsData, notesData) {
		let sunmiStore = useSunmiStore();
		sunmiStore.cashmaticFloatLevels = {
			success: success,
			coins: coinsData,
			notes: notesData
		};
	}

	/**
	 * Called when float levels are set.
	 * @param {boolean} success - Whether the operation succeeded
	 * @param {string} type - 'coins' or 'notes'
	 * @param {string} errorMessage - Error message if failed
	 */
	window.cashmaticFloatLevelSet = function (success, type, errorMessage) {
		let sunmiStore = useSunmiStore();
		sunmiStore.cashmaticFloatLevelSetResult = {
			success: success,
			type: type,
			error: errorMessage || null
		};
	}

	// ==========================================
	// CASHMATIC ALL LEVELS CALLBACK
	// ==========================================

	/**
	 * Called when all denomination levels are received.
	 * @param {boolean} success - Whether the request succeeded
	 * @param {Object} coinsData - Level data for coins {items: [{value, level, type, currency}]}
	 * @param {Object} notesData - Level data for notes {items: [{value, level, type, currency}]}
	 */
	window.cashmaticAllLevelsReceived = function (success, coinsData, notesData) {
		let sunmiStore = useSunmiStore();
		sunmiStore.cashmaticAllLevels = {
			success: success,
			coins: coinsData,
			notes: notesData
		};
	}
}
